import { useEffect, useState, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Save, X, Printer, FileText, Info } from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  save_restaurant_setting,
  get_restaurant_setting,
} from "../ServiceConnection/serviceconnection";

export default function KotPrint() {
  const { toast } = useToast();

  // State management
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [isPrintAddonEnabled, setIsPrintAddonEnabled] = useState(false);
  const [originalValue, setOriginalValue] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch settings data
  const fetchSettingsData = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag", 1);
    fd.append("want_table_data", 0);

    try {
      const response = await server_post_data(get_restaurant_setting, fd);

      if (!response.data.error) {
        const value =
          response.data.message.data_restaurant?.[0]?.print_addon === "true";
        setIsPrintAddonEnabled(value);
        setOriginalValue(value);
        setHasChanges(false);
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast({
        title: "Error",
        description: "Failed to load settings data",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [toast]);

  // Handle toggle change
  const handleToggleChange = useCallback(
    (checked) => {
      setIsPrintAddonEnabled(checked);
      setHasChanges(checked !== originalValue);
    },
    [originalValue]
  );

  // Handle save
  const handleSave = useCallback(async () => {
    if (!hasChanges) {
      toast({
        title: "No Changes",
        description: "No changes to save",
      });
      return;
    }

    setShowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("print_addon", isPrintAddonEnabled);
    fd.append("from_where", 3);

    try {
      const response = await server_post_data(save_restaurant_setting, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "KOT print settings saved successfully",
        });
        setOriginalValue(isPrintAddonEnabled);
        setHasChanges(false);
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [isPrintAddonEnabled, hasChanges, toast]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    setIsPrintAddonEnabled(originalValue);
    setHasChanges(false);
  }, [originalValue]);

  // Initial load
  useEffect(() => {
    fetchSettingsData();
  }, [fetchSettingsData]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">
          KOT Print Settings
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Configure how items are displayed on Kitchen Order Tickets
        </p>
      </div>

      {/* Print Add-ons Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Printer className="w-4 h-4 text-primary" />
            Add-ons & Special Notes
          </CardTitle>
          <CardDescription>
            Control how add-ons and special notes appear in KOT
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-muted/30">
            <div className="space-y-1 flex-1">
              <Label
                htmlFor="print-addon-switch"
                className="text-sm font-medium cursor-pointer"
              >
                Print add-ons and special notes below item row
              </Label>
              <p className="text-sm text-muted-foreground">
                When enabled, add-ons and special notes will be printed below
                the item name in the KOT
              </p>
            </div>
            <Switch
              id="print-addon-switch"
              checked={isPrintAddonEnabled}
              onCheckedChange={handleToggleChange}
            />
          </div>

          {/* Info Alert */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Print add-ons and special notes for particular items below the
              item name row in KOT. This helps kitchen staff identify specific
              customer requests and modifications.
            </AlertDescription>
          </Alert>

          {/* Status Message */}
          {hasChanges && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-warning/10 border border-warning/20">
              <FileText className="w-4 h-4 text-warning" />
              <p className="text-sm text-warning">
                You have unsaved changes. Click Save to apply.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Example Card */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-foreground">
                KOT Print Example
              </h4>
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-background border border-border">
                  <p className="text-sm font-medium text-foreground mb-1">
                    When Disabled:
                  </p>
                  <div className="text-sm text-muted-foreground font-mono">
                    Margherita Pizza
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-background border border-border">
                  <p className="text-sm font-medium text-foreground mb-1">
                    When Enabled:
                  </p>
                  <div className="text-sm text-muted-foreground font-mono space-y-1">
                    <div>Margherita Pizza</div>
                    <div className="pl-4 text-xs">+ Extra Cheese</div>
                    <div className="pl-4 text-xs">+ No Onions</div>
                    <div className="pl-4 text-xs italic">
                      Note: Make it extra crispy
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-4 pt-4 border-t">
        <Button
          variant="outline"
          onClick={handleCancel}
          disabled={showLoaderAdmin || !hasChanges}
          className="gap-2"
        >
          <X className="w-4 h-4" />
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={showLoaderAdmin || !hasChanges}
          className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
        >
          {showLoaderAdmin ? (
            "Saving..."
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
